import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetLotTypes } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { CONSTANT_LOT_TYPE_GUIDS } from '../../../appConstants';
import { ILotType, LotType } from '../../../@types/model/masterData/lotType/lotType';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import LotTypeHttpService from '../../../services/http/masterData/lotTypeHttpService';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { LotTypeFormValues, ILotTypeFormValues } from '../../../@types/model/masterData/lotType/lotTypeFormValues';
import LotTypeForm from './form/LotTypeForm';
import { useHistory } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import { setLotTypeMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const LotTypeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const lotTypes = useAppSelector(x => x.masterData.lotTypes);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedLotType, setSelectedLotType] = useState<ILotType>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingLotType, setDeletingLotType] = useState<ILotType>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await LotTypeHttpService.getLotTypeData();
                    dispatch(dataSetLotTypes(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading lot types.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedLotType(undefined);
        setFormGuid(undefined);
    };

    const editLotType = (lotType : ILotType) => {
        setSelectedLotType(lotType);
        setFormGuid(lotType.guid);
        openDialog();
    };

    const onSubmit = async (values : ILotTypeFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await LotTypeHttpService.addOrUpdateLotType(LotType(payload));
            const updatedList = upsertArrayElement(lotTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetLotTypes(updatedList));
            await setLotTypeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedLotType ? 'Lot Type updated successfully.' : 'Lot Type added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating Lot Type data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = (lotType : ILotType) => {
        setDeletingLotType(lotType);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingLotType(undefined);
        setIsDeletePopupOpen(false);
    };

    const removeLotType = async () => {
        if (!deletingLotType) return;
        const updatedLotType = { ...deletingLotType, isActive: false };
        setIsLoading(true);
        try {
            const res = await LotTypeHttpService.deleteLotType(updatedLotType.id);
            const updatedList = upsertArrayElement(lotTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetLotTypes(updatedList));
            await setLotTypeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Lot Type deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting Lot Type.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const isEditable = useCallback((lotType : ILotType) => {
        return !CONSTANT_LOT_TYPE_GUIDS.some(x => x === lotType.guid);
    }, []);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return LotTypeFormValues(selectedLotType);
        }
    }, [selectedLotType, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Lot Type'
                    isEdit={!!selectedLotType}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <LotTypeForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<ILotType>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editLotType}
                        enableEditing={lt => isEditable(lt) && hasEditingRight}
                        enableDeleting={lt => lt.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Swappable?', field: 'isSwappable', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={lotTypes}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Lot Type'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this lot type?'
                onAccept={removeLotType}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default LotTypeScreen;
